import type { CircleBuddyMood } from "../components/CircleBuddy";
import { clampPercent } from "./progress";

/**
 * The five tiers mastery is spoken in, and the one place they are defined.
 *
 * The student NEVER sees a mastery number. 0.19 is meaningless to them and worse
 * than meaningless in a sentence — "você está com 0,19 em Cinemática" reads as a
 * grade out of one. They see a tier, and the tier is the whole language the product
 * has for strength: one colour ramp from coral (a difficulty) through amber and blue
 * to green (a strength), worn by the pill, the meter and the callouts alike, so a
 * glance down a page reads red-to-green without a number being parsed.
 *
 * FOUR PRACTISED TIERS AND A FIFTH FOR NOTHING. `null` mastery means never answered,
 * and it must never render as "Começando" — that claims a start that did not happen.
 * The thresholds are the agent's too (they are written into lume-prompt.ts, which
 * forbids it from quoting the raw number), so a change here is a change in two repos.
 *
 * ── WHY IT LIVES IN lib/ ────────────────────────────────────────────────────
 * It was local to pages/caderno/Pratica.tsx while that dashboard was the only
 * surface speaking in tiers. The chat's progress card is the second, and two copies
 * of four thresholds is how two screens end up disagreeing about whether 0.6 is "Em
 * progresso" or "Sólido" — a disagreement nothing would catch, because both would
 * look right on their own.
 */
export interface MasteryTier {
  label: string;
  /** Border + text colour for the pill */
  className: string;
  /** Text-only colour for the inline status word beside a meter */
  text: string;
  /** Solid fill for a filled meter segment (and the legend dot) */
  bar: string;
  /** Tinted background for a callout in this tier's colour */
  soft: string;
  /** The face this tier wears — the buddy that stands in for an emoji */
  mood: CircleBuddyMood;
}

/** Below this a practised score is "Começando" */
export const COMECANDO_BELOW = 0.35;
/** From this up a score is "Dominado" */
export const DOMINADO_FROM = 0.85;

export function masteryTier(mastery: number | null): MasteryTier {
  if (mastery === null) {
    return {
      className: "border-ink/15 text-neutral-500",
      text: "text-neutral-500",
      label: "Sem prática",
      bar: "bg-ink/20",
      soft: "bg-ink/5",
      mood: "idle",
    };
  }
  if (mastery < COMECANDO_BELOW) {
    return {
      className: "border-coral-500/45 text-coral-600",
      text: "text-coral-600",
      label: "Começando",
      bar: "bg-coral-500",
      soft: "bg-coral-500/10",
      mood: "sad",
    };
  }
  if (mastery < 0.6) {
    return {
      className: "border-warning-500/50 text-warning-600",
      text: "text-warning-600",
      label: "Em progresso",
      bar: "bg-warning-500",
      soft: "bg-warning-500/12",
      mood: "thinking",
    };
  }
  if (mastery < DOMINADO_FROM) {
    return {
      className: "border-info-800/40 text-info-800",
      text: "text-info-800",
      label: "Sólido",
      bar: "bg-info-700",
      soft: "bg-info-700/12",
      mood: "nodding",
    };
  }
  return {
    className: "border-success-600/50 text-success-600",
    text: "text-success-600",
    label: "Dominado",
    bar: "bg-success-600",
    soft: "bg-success-600/12",
    mood: "celebrating",
  };
}

export const TIER_PILL =
  "shrink-0 rounded-full border px-2.5 py-1 font-mono text-[10px]/none font-semibold tracking-[0.06em] uppercase";

export const METER_SEGMENTS = 5;

/**
 * How many of the meter's segments are filled.
 *
 * Anything practised fills at least one, so a barely-started matéria never reads as
 * a blank row; null leaves them all empty, because there is no score to draw.
 */
export function meterFill(mastery: number | null): number {
  return mastery === null
    ? 0
    : Math.max(
        1,
        Math.round((clampPercent(mastery * 100) / 100) * METER_SEGMENTS)
      );
}
